import { useCallback, useState } from "react";
import moment from "moment";
import toast from "react-hot-toast";
import { useQueryClient } from "react-query";
import {
  createSchedulerEvent,
  getEventListDetails,
  updateSchedulerEvent,
} from "repositories/scheduler-repository";
import {
  analyzeQuickEditChanges,
  buildQuickEditCreateParams,
  buildQuickEditUpdateParams,
  resolveQuickEditInitialOffice,
} from "./quickEdit.utils";

/**
 * Quick Edit state + save for Weekly view by staff name cards.
 *
 * @param {object} options
 * @param {function} options.t
 * @param {number|string} options.ownerId
 * @param {function} [options.onSuccess]
 */
export function useQuickEditFromCard({ t, ownerId, onSuccess }) {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [context, setContext] = useState(null);

  const openQuickEdit = useCallback((eventItem, requestedDate) => {
    if (!eventItem?.id) return;

    const { officeId, officeName } = resolveQuickEditInitialOffice(eventItem);

    setContext({
      eventItem,
      requestedDate: moment(requestedDate).format("YYYY-MM-DD"),
      userId: eventItem?.userId ?? eventItem?.user?.id ?? null,
      initialOfficeId: officeId,
      initialOfficeName:
        officeName ||
        eventItem?.officeName ||
        eventItem?.eventEmployeeOfficeName ||
        eventItem?.office?.name ||
        eventItem?.office?.officeName ||
        "",
      initialDate: requestedDate,
      initialStartTime: eventItem?.startTime,
      initialEndTime: eventItem?.endTime,
    });
    setIsOpen(true);
  }, []);

  const closeQuickEdit = useCallback(() => {
    if (isSaving) return;
    setIsOpen(false);
    setContext(null);
  }, [isSaving]);

  const saveQuickEdit = useCallback(
    async ({ officeId, eventDate, startTime, endTime }) => {
      if (!context?.eventItem?.id) return;

      setIsSaving(true);
      try {
        const eventDetails = await getEventListDetails(
          context.eventItem.id,
          context.requestedDate,
        );
        const eventData = eventDetails?.data;
        if (!eventData) {
          toast.error(t("scheduler.eventNotFound"));
          return;
        }

        const analysis = analyzeQuickEditChanges({
          eventData,
          requestedDate: context.requestedDate,
          selectedUserId: context.userId,
          officeId,
          eventDate,
          startTime,
          endTime,
        });

        if (
          !analysis.isOfficeChanged &&
          !analysis.hasDateTimeChange
        ) {
          setIsOpen(false);
          setContext(null);
          return;
        }

        if (analysis.shouldSplit) {
          const removeParams = buildQuickEditUpdateParams({
            eventData,
            ownerId,
            requestedDate: context.requestedDate,
            eventDate,
            selectedUserId: context.userId,
            analysis,
            keepOriginalDateTime: true,
            removeSelectedUser: true,
          });
          await updateSchedulerEvent(removeParams);

          const createParams = buildQuickEditCreateParams({
            eventData,
            ownerId,
            selectedUserId: context.userId,
            analysis,
            eventDate,
          });
          await createSchedulerEvent(createParams);
        } else {
          const updateParams = buildQuickEditUpdateParams({
            eventData,
            ownerId,
            requestedDate: context.requestedDate,
            eventDate,
            selectedUserId: context.userId,
            analysis,
            keepOriginalDateTime: false,
            removeSelectedUser: false,
          });
          await updateSchedulerEvent(updateParams);
        }

        queryClient.invalidateQueries(["office_agenda"]);
        queryClient.invalidateQueries(["event_list_assigner_by_employee"]);
        onSuccess?.();
        toast.success(t("staff.eventUpdated"));
        setIsOpen(false);
        setContext(null);
      } catch (error) {
        toast.error(error?.message || t("scheduler.updateFailed"));
      } finally {
        setIsSaving(false);
      }
    },
    [context, ownerId, onSuccess, queryClient, t],
  );

  return {
    isQuickEditOpen: isOpen,
    isQuickEditSaving: isSaving,
    quickEditContext: context,
    openQuickEdit,
    closeQuickEdit,
    saveQuickEdit,
  };
}
